import React, { useState, useEffect } from 'react';

// ==========================================
// 1. THEME & STYLES
// ==========================================
export const colors = {
  bg: '#020617', cardBg: '#0f172a', border: '#1e293b',
  textMain: '#f8fafc', textSub: '#94a3b8',
  green: '#22c55e', greenBg: 'rgba(34, 197, 94, 0.1)',
  red: '#ef4444', redBg: 'rgba(239, 68, 68, 0.1)',
  warn: '#eab308', warnBg: 'rgba(234, 179, 8, 0.1)'
};

export const baseStyles = {
  container: { backgroundColor: colors.bg, color: colors.textMain, padding: '16px', fontFamily: 'system-ui, sans-serif', minHeight: '100vh', boxSizing: 'border-box' },
  card: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5)' },
  input: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, color: colors.textMain, padding: '8px 12px', borderRadius: '6px', outline: 'none', width: '100%', maxWidth: '200px' },
  select: { backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.textMain, padding: '6px 12px', borderRadius: '6px', outline: 'none', cursor: 'pointer' },
  liveBadge: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: colors.cardBg, padding: '6px 12px', borderRadius: '20px', border: `1px solid ${colors.border}`, fontSize: '12px', color: colors.green, fontWeight: 'bold', whiteSpace: 'nowrap' },
  dot: { width: '8px', height: '8px', backgroundColor: colors.green, borderRadius: '50%', animation: 'pulse 1.5s infinite' },
  flexBetween: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', marginTop: '8px', flexWrap: 'wrap', gap: '4px' },
  sectionTitle: { fontSize: '12px', color: colors.textSub, textTransform: 'uppercase', marginBottom: '12px', marginTop: '16px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '8px' },
  tableContainer: { overflowX: 'auto', paddingBottom: '8px' }, // Đổi thành auto để scroll ngang nếu tràn
  tableGrid: { display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1.3fr 1.1fr 1.2fr', gap: '4px', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, paddingBottom: '8px', marginBottom: '8px' },
  row: { display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1.3fr 1.1fr 1.2fr', gap: '4px', padding: '6px 0' },
};

// ==========================================
// 2. UTILS
// ==========================================
export const formatUSD = (val) => {
  if (val === undefined || val === null || isNaN(val)) return '$0';
  const absVal = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (absVal >= 1e9) return `${sign}${(absVal / 1e9).toFixed(2)}B`;
  if (absVal >= 1e6) return `${sign}${(absVal / 1e6).toFixed(2)}M`;
  if (absVal >= 1e3) return `${sign}${(absVal / 1e3).toFixed(2)}K`;
  return `${sign}$${absVal.toFixed(2)}`;
};

export const formatSmartPrice = (val) => {
  if (!val) return '0';
  const num = parseFloat(val);
  if (num >= 1000) return `${formatNum(num, 2)}`;
  if (num >= 1) return `${formatNum(num, 4)}`;
  if (num >= 0.001) return `${formatNum(num, 6)}`;
  return `${formatNum(num, 8)}`;
};

// Thêm tham số decimals để custom số thập phân thay vì fix cứng 4
export const formatNum = (val, decimals = 4) => {
  if (val === undefined || val === null || isNaN(val)) return '0';
  return val.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

// ==========================================
// 3. HOOKS
// ==========================================
export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({ width: typeof window !== 'undefined' ? window.innerWidth : 1200 });
  useEffect(() => {
    function handleResize() { setWindowSize({ width: window.innerWidth }); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowSize;
}

// ==========================================
// 4. COMPONENTS
// ==========================================

export const DeltaBadge = ({ current, base, prefix = '', style = {}, className = '', ...props }) => {
  // Tránh lỗi chia cho 0 nếu base = 0
  if (base === undefined || base === null || current === undefined || current === null || base === 0) {
    return <span className={className} style={{ color: colors.textSub, fontSize: '11px', marginLeft: '4px', ...style }} {...props}>-</span>;
  }
  
  if (current === base) {
    return <span className={className} style={{ color: colors.textSub, fontSize: '11px', marginLeft: '4px', ...style }} {...props}>=</span>;
  }

  const percent = (((current - base) / Math.abs(base)) * 100).toFixed(2);
  const isUp = current > base;
  
  return (
    <span 
      className={className}
      style={{ color: isUp ? colors.green : colors.red, fontSize: '11px', marginLeft: '4px', fontWeight: 'bold', whiteSpace: 'nowrap', ...style }} 
      {...props}
    >
      {isUp ? '▲' : '▼'} {prefix}{Math.abs(percent)}%
    </span>
  );
};

export const LiveCompareBadge = ({ 
  liveVal, 
  histVal, 
  type = 'percent', 
  style = {}, 
  className = '',
  // Đưa text cứng ra ngoài làm props để có thể đổi sang tiếng Anh hoặc text khác
  labels = { breakout: 'Đã phá đỉnh', below: 'Dưới đỉnh' },
  ...props
}) => {
  if (liveVal === undefined || histVal === undefined || isNaN(histVal) || isNaN(liveVal)) return null;
  
  const isUp = liveVal >= histVal;
  const color = isUp ? colors.green : colors.red;
  const badgeStyle = { color, fontSize: '11px', ...style };

  if (type === 'percent') {
    // Xử lý lỗi chia cho 0
    if (histVal === 0) return null; 
    const pct = (((liveVal - histVal) / Math.abs(histVal)) * 100).toFixed(2);
    return <span className={className} style={badgeStyle} {...props}>({isUp ? '🟢' : '🔴'} {pct > 0 ? '+' : ''}{pct}%)</span>;
  }
  
  if (type === 'breakout') {
    if (histVal === 0) return null;
    const pctDiff = (((histVal - liveVal) / Math.abs(histVal)) * 100).toFixed(1);
    return (
      <span className={className} style={badgeStyle} {...props}>
        {isUp ? `(🟢 ${labels.breakout})` : `(🔴 ${labels.below} ${pctDiff}%)`}
      </span>
    );
  }
  
  if (type === 'cvd') {
    const diff = liveVal - histVal;
    return <span className={className} style={badgeStyle} {...props}>({diff > 0 ? '🟢 +' : '🔴 '}{formatUSD(diff)})</span>;
  }
  
  return null;
};

export const MetricCard = ({ 
  label, 
  currentVal, 
  baseVal, 
  format = 'number', // 'number', 'usd', hoặc một function(val)
  valueColor,
  decimals = 4, // Cho phép custom số thập phân
  style = {}, 
  className = '',
  ...props
}) => {
  // Hàm xử lý hiển thị format linh hoạt
  const formatValue = (val) => {
    if (typeof format === 'function') return format(val);
    if (format === 'usd') return formatUSD(val);
    return formatNum(val, decimals);
  };

  const displayCurrent = formatValue(currentVal);
  const displayBase = formatValue(baseVal);

  return (
    <div className={className} style={{ ...baseStyles.card, ...style }} {...props}>
      <div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
        <span style={{ color: valueColor || colors.textMain }}>{displayCurrent}</span> 
        <DeltaBadge current={currentVal} base={baseVal} />
      </div>
      {baseVal !== undefined && baseVal !== null && (
        <div style={{ fontSize: '10px', color: colors.textSub, marginTop: '4px' }}>
          (Base: {displayBase})
        </div>
      )}
    </div>
  );
};
